import React, { useCallback, useContext, useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs, limit, orderBy, query, startAfter } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import MobileNavTabs from '../components/MobileNavTabs';
import DesktopSidebar from '../components/DesktopSidebar';
import ResolvedStorageImage from '../components/ResolvedStorageImage';
import desktopLayout from '../components/DesktopSidebarLayout.module.css';
import layoutStyles from '../styles/layout.module.css';
import { db } from '../firebase';
import { UserContext } from '../App';
import styles from './FanFollowing.module.css';
import { getArtistPhotoPath } from '../utils/storageMedia';

const PAGE_SIZE = 10;

export default function FanFollowing() {
  const appUser = useContext(UserContext);
  const navigate = useNavigate();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const loadPage = useCallback(async ({ reset = false, afterDoc = null } = {}) => {
    if (!appUser?.id) return;

    const setBusy = reset ? setLoading : setLoadingMore;
    setBusy(true);
    if (reset) setError('');

    try {
      const baseQuery = query(
        collection(db, 'users', appUser.id, 'follows'),
        orderBy('createdAt', 'desc'),
        ...(afterDoc ? [startAfter(afterDoc), limit(PAGE_SIZE)] : [limit(PAGE_SIZE)])
      );
      const followsSnap = await getDocs(baseQuery);
      const rows = await Promise.all(followsSnap.docs.map(async (followDoc) => {
        const data = followDoc.data() || {};
        const artistId = String(data.artistId || followDoc.id || '').trim();
        if (!artistId) return null;
        const artistSnap = await getDoc(doc(db, 'artists', artistId));
        if (!artistSnap.exists()) return null;
        const artist = artistSnap.data() || {};
        return {
          id: artistSnap.id,
          artistId,
          createdAt: data.createdAt,
          name: artist.artistFullName || 'Untitled Artist',
          photoPath: getArtistPhotoPath(artist, 'full'),
        };
      }));

      const validRows = rows.filter(Boolean);
      setArtists((prev) => (reset ? validRows : [...prev, ...validRows]));
      setCursor(followsSnap.docs[followsSnap.docs.length - 1] || null);
      setHasMore(followsSnap.docs.length === PAGE_SIZE);
    } catch (err) {
      setError(err?.message || 'Unable to load followed artists.');
    } finally {
      setBusy(false);
    }
  }, [appUser?.id]);

  useEffect(() => {
    setArtists([]);
    setCursor(null);
    setHasMore(false);
    if (appUser?.id) loadPage({ reset: true });
  }, [appUser?.id, loadPage]);

  return (
    <div className={layoutStyles.homeContainer} style={{ paddingBottom: 0 }}>
      <TopBar hideLeft>
        <div className={desktopLayout.mobileTabs}><MobileNavTabs /></div>
      </TopBar>
      <div className={`${desktopLayout.shell} ${desktopLayout.shellWithRail}`.trim()}>
        <DesktopSidebar active="fan" note="The artists you follow live here, with room to keep exploring new drops." />
        <main className={desktopLayout.main}>
          <div className={desktopLayout.mainInner}>
            <div className={styles.pageShell}>
              <section className={styles.heroCard}>
                <span className={styles.eyebrow}>Following</span>
                <h1>Artists you follow</h1>
                <p>Keep track of the artists already on your radar and jump back into their live collectible pages whenever a countdown catches your eye.</p>
              </section>

              {loading ? <p className={styles.state}>Loading followed artists…</p> : null}
              {error ? <p className={styles.error}>{error}</p> : null}

              {!loading && !error && artists.length ? (
                <>
                  <div className={styles.grid}>
                    {artists.map((artist) => (
                      <button key={artist.id} type="button" className={styles.card} onClick={() => navigate(`/artists/${artist.artistId}`)}>
                        <div className={styles.media}>
                          <ResolvedStorageImage path={artist.photoPath} alt={artist.name} fallback={<div className={styles.placeholder}>Artist</div>} />
                          <div className={styles.overlay} />
                        </div>
                        <div className={styles.body}>
                          <h2>{artist.name}</h2>
                        </div>
                      </button>
                    ))}
                  </div>
                  {hasMore ? (
                    <div className={styles.loadMoreRow}>
                      <button type="button" className={styles.loadMoreButton} onClick={() => loadPage({ afterDoc: cursor })} disabled={loadingMore}>
                        {loadingMore ? 'Loading…' : 'Load 10 More'}
                      </button>
                    </div>
                  ) : null}
                </>
              ) : null}

              {!loading && !error && !artists.length ? (
                <section className={styles.emptyState}>
                  <span className={styles.emptyBadge}>No followed artists yet</span>
                  <h2>Your following list is still empty.</h2>
                  <p>Find artists on the home page, hit Become a Fan, and this page will turn into your shortcut to every live drop you care about.</p>
                  <button type="button" className={styles.primaryButton} onClick={() => navigate('/home')}>Browse Artists</button>
                </section>
              ) : null}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
